import React from 'react'
import { Tag, Space, Button, Progress } from 'antd'
import {
  FilePdfOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined
} from '@ant-design/icons'

// Column definitions for Rehabilitation Exercises table
export const rehabExercisesColumns = [
  {
    title: 'Exercise',
    dataIndex: 'name',
    key: 'name',
    width: '20%'
  },
  {
    title: 'Target Area',
    dataIndex: 'targetArea',
    key: 'targetArea',
    width: '10%'
  },
  {
    title: 'Frequency',
    dataIndex: 'frequency',
    key: 'frequency',
    width: '12%'
  },
  {
    title: 'Sets x Reps',
    key: 'setsReps',
    width: '12%',
    render: (_, record) => (
      <span>
        {record.sets} x {record.repetitions}
      </span>
    )
  },
  {
    title: 'Duration',
    dataIndex: 'duration',
    key: 'duration',
    width: '10%',
    render: (duration) => `${duration} min`
  },
  {
    title: 'Instructions',
    dataIndex: 'description',
    key: 'description',
    width: '25%'
  },
  {
    title: 'Adherence',
    key: 'adherence',
    width: '15%',
    render: (_, record) => (
      <Progress
        percent={record.adherence || 0}
        size='small'
        status={record.adherence >= 75 ? 'success' : record.adherence >= 50 ? 'normal' : 'exception'}
      />
    )
  }
]

// Column definitions for Pain Medication table
export const painMedicationColumns = [
  {
    title: 'Medication',
    dataIndex: 'name',
    key: 'name',
    width: '18%'
  },
  {
    title: 'Dosage',
    key: 'dosage',
    width: '12%',
    render: (_, record) => `${record.dosage} ${record.unit}`
  },
  {
    title: 'Frequency',
    dataIndex: 'frequency',
    key: 'frequency',
    width: '15%'
  },
  {
    title: 'Duration',
    dataIndex: 'duration',
    key: 'duration',
    width: '12%'
  },
  {
    title: 'Pain Level',
    dataIndex: 'painLevel',
    key: 'painLevel',
    width: '10%',
    render: (painLevel) => {
      const color = painLevel <= 3 ? 'green' : painLevel <= 7 ? 'orange' : 'red'
      return <Tag color={color}>{painLevel}/10</Tag>
    }
  },
  {
    title: 'Instructions',
    dataIndex: 'instructions',
    key: 'instructions',
    width: '20%'
  },
  {
    title: 'Adherence',
    key: 'adherence',
    width: '15%',
    render: (_, record) => (
      <Progress
        percent={record.adherence || 0}
        size='small'
        status={record.adherence >= 75 ? 'success' : record.adherence >= 50 ? 'normal' : 'exception'}
      />
    )
  }
]

// Column definitions for Functional Assessments table
export const functionalAssessmentsColumns = [
  {
    title: 'Assessment',
    dataIndex: 'name',
    key: 'name',
    width: '20%'
  },
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'category',
    width: '12%',
    render: (category) => {
      const colors = {
        Mobility: 'blue',
        Strength: 'purple',
        Balance: 'green',
        Endurance: 'orange',
        Pain: 'red',
        Function: 'cyan',
        Gait: 'geekblue'
      }
      return <Tag color={colors[category] || 'default'}>{category}</Tag>
    }
  },
  {
    title: 'Scheduled Date',
    dataIndex: 'scheduledDate',
    key: 'scheduledDate',
    width: '12%',
    render: (date) => (date ? date.format('DD/MM/YYYY') : '')
  },
  {
    title: 'Baseline',
    dataIndex: 'baseline',
    key: 'baseline',
    width: '10%'
  },
  {
    title: 'Target',
    dataIndex: 'target',
    key: 'target',
    width: '10%'
  },
  {
    title: 'Result',
    dataIndex: 'result',
    key: 'result',
    width: '10%',
    render: (result) => (result !== null ? result : '-')
  },
  {
    title: 'Status',
    key: 'completed',
    width: '10%',
    render: (_, record) => (
      <Tag color={record.completed ? 'green' : 'blue'}>{record.completed ? 'Completed' : 'Pending'}</Tag>
    )
  },
  {
    title: 'Progress',
    key: 'progress',
    width: '16%',
    render: (_, record) => {
      if (!record.completed || record.result === null) return '-'

      const baseline = record.baseline || 0
      const target = record.target || 100
      const result = record.result

      // Calculate progress as percentage of target achieved from baseline
      const totalImprovement = target - baseline
      const actualImprovement = result - baseline
      const progressPercent = Math.round((actualImprovement / totalImprovement) * 100)

      // Clamp between 0-100%
      const clampedProgress = Math.max(0, Math.min(100, progressPercent))

      return (
        <Progress
          percent={clampedProgress}
          size='small'
          status={clampedProgress >= 90 ? 'success' : clampedProgress >= 50 ? 'normal' : 'exception'}
        />
      )
    }
  }
]

// Column definitions for Psychological Support table
export const psychologicalSupportColumns = [
  {
    title: 'Resource',
    dataIndex: 'name',
    key: 'name',
    width: '20%'
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: '12%',
    render: (type) => {
      const colors = {
        'Self-help': 'green',
        Assessment: 'purple',
        Therapy: 'blue',
        Mindfulness: 'cyan',
        'Support group': 'gold',
        Educational: 'geekblue',
        'Mind-body': 'magenta',
        'Individual counseling': 'volcano',
        'Group session': 'orange',
        Workbook: 'lime'
      }
      return <Tag color={colors[type] || 'default'}>{type}</Tag>
    }
  },
  {
    title: 'Recommended Date',
    dataIndex: 'recommendedDate',
    key: 'recommendedDate',
    width: '12%',
    render: (date) => (date ? date.format('DD/MM/YYYY') : '')
  },
  {
    title: 'Priority',
    dataIndex: 'priority',
    key: 'priority',
    width: '10%',
    render: (priority) => {
      const colors = {
        High: 'red',
        Medium: 'orange',
        Low: 'green'
      }
      return <Tag color={colors[priority] || 'default'}>{priority}</Tag>
    }
  },
  {
    title: 'Notes',
    dataIndex: 'notes',
    key: 'notes',
    width: '20%'
  },
  {
    title: 'Contact Info',
    dataIndex: 'contactInfo',
    key: 'contactInfo',
    width: '15%'
  },
  {
    title: 'Status',
    key: 'completed',
    width: '11%',
    render: (_, record) => (
      <Space>
        {record.completed ? (
          <Tag icon={<CheckCircleOutlined />} color='success'>
            Completed
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color='default'>
            Pending
          </Tag>
        )}
        {record.followUp && (
          <Tag icon={<WarningOutlined />} color='warning'>
            Follow-up
          </Tag>
        )}
      </Space>
    )
  }
]

// Column definitions for Patient Education Resources table
export const educationResourcesColumns = [
  {
    title: 'Resource',
    dataIndex: 'name',
    key: 'name',
    width: '20%',
    render: (text, record) => {
      let icon = <FileTextOutlined style={{ color: '#52c41a' }} />
      if (record.format?.toLowerCase() === 'pdf') {
        icon = <FilePdfOutlined style={{ color: '#ff4d4f' }} />
      } else if (record.format?.toLowerCase() === 'video') {
        icon = <PlayCircleOutlined style={{ color: '#1890ff' }} />
      }

      return (
        <Space>
          {icon} {text}
        </Space>
      )
    }
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: '12%'
  },
  {
    title: 'Format',
    dataIndex: 'format',
    key: 'format',
    width: '8%',
    render: (format) => <Tag color={format === 'PDF' ? 'red' : format === 'Video' ? 'blue' : 'green'}>{format}</Tag>
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    width: '25%',
    ellipsis: true
  },
  {
    title: 'Importance',
    dataIndex: 'importance',
    key: 'importance',
    width: '10%',
    render: (importance) => {
      const colors = {
        Essential: 'red',
        Recommended: 'orange',
        Optional: 'green'
      }
      return <Tag color={colors[importance] || 'default'}>{importance}</Tag>
    }
  },
  {
    title: 'Instructions',
    dataIndex: 'instructions',
    key: 'instructions',
    width: '15%'
  },
  {
    title: 'Action',
    key: 'action',
    width: '10%',
    render: (_, record) => (
      <Button type='link' href={record.url} target='_blank'>
        {record.format === 'Video' ? 'Watch' : 'View'}
      </Button>
    )
  }
]
